import React, { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../api';

const TEAL   = '#14b8a6';
const GREEN  = '#34d399';
const AMBER  = '#f59e0b';
const RED    = '#ef4444';
const CARD   = '#1e2329';
const SUNK   = '#171b21';
const BORDER = '#2a3040';
const MUTED  = '#6b7280';
const TEXT   = '#e5e7eb';
const DIM    = '#9ca3af';

const st = {
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.78)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1200, fontFamily: 'system-ui', padding: 20 },
  card:    { background: CARD, border: `1px solid ${BORDER}`, borderRadius: 16, width: '100%', maxWidth: 720, maxHeight: '90vh', display: 'flex', flexDirection: 'column' },
  head:    { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '18px 22px 0', flexShrink: 0 },
  title:   { fontSize: 15, fontWeight: 700, color: TEXT, display: 'flex', alignItems: 'center', gap: 9 },
  tabs:    { display: 'flex', gap: 2, padding: '14px 22px 0', borderBottom: `1px solid ${BORDER}`, flexShrink: 0 },
  body:    { padding: '18px 22px 24px', overflowY: 'auto', flex: 1 },
  footer:  { padding: '13px 22px', display: 'flex', gap: 8, justifyContent: 'flex-end', borderTop: `1px solid ${BORDER}`, flexShrink: 0 },
  btnGhost:{ padding: '9px 16px', background: 'transparent', color: MUTED, border: `1px solid ${BORDER}`, borderRadius: 8, cursor: 'pointer', fontSize: 13 },
  eyebrow: { fontSize: 10, color: MUTED, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.09em' },
};

// How each kind of event reads. Reality is green: a job starting or finishing
// happened, where everything else is a decision someone made about the future.
const KIND = {
  completed: { icon: 'fa-flag-checkered',            colour: GREEN, label: 'Finished' },
  started:   { icon: 'fa-play',                      colour: GREEN, label: 'Started' },
  scheduled: { icon: 'fa-calendar-plus',             colour: TEAL,  label: 'Scheduled' },
  reflow:    { icon: 'fa-arrows-left-right-to-line', colour: TEAL,  label: 'Re-flowed' },
  moved:     { icon: 'fa-right-left',                colour: TEAL,  label: 'Moved' },
  resized:   { icon: 'fa-arrows-left-right',         colour: TEAL,  label: 'Resized' },
  replanned: { icon: 'fa-pen-to-square',             colour: TEAL,  label: 'Re-planned' },
  split:     { icon: 'fa-scissors',                  colour: TEAL,  label: 'Split' },
  duplicated:{ icon: 'fa-copy',                      colour: TEAL,  label: 'Copied' },
  quantity:  { icon: 'fa-hashtag',                   colour: TEAL,  label: 'Quantity' },
  // What a change moved out of its way -- linked to that change, never a step
  // of its own (migration 041).
  pushed:    { icon: 'fa-arrow-right-long',          colour: MUTED, label: 'Pushed' },
  window:    { icon: 'fa-layer-group',               colour: AMBER, label: 'Shift window' },
  hold:      { icon: 'fa-lock',                      colour: AMBER, label: 'Hold' },
  removed:   { icon: 'fa-trash',                     colour: RED,   label: 'Removed' },
  restored:  { icon: 'fa-rotate-left',               colour: MUTED, label: 'Restored' },
  put_back:  { icon: 'fa-clock-rotate-left',         colour: AMBER, label: 'Put back' },
  undone:    { icon: 'fa-rotate-left',               colour: MUTED, label: 'Undone' },
  redone:    { icon: 'fa-rotate-right',              colour: MUTED, label: 'Redone' },
};
const kindOf = k => KIND[k] || { icon: 'fa-circle', colour: MUTED, label: k };


function dayLabel(iso) {
  const d = new Date(iso);
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const that = new Date(d); that.setHours(0, 0, 0, 0);
  const diff = Math.round((today - that) / 86400000);
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Yesterday';
  return d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: that.getFullYear() === today.getFullYear() ? undefined : 'numeric' });
}
const timeOf = iso => new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });

// Whether the record has been altered, checked each time the history opens.
//
// Every entry carries a fingerprint chained to the one before it (migration
// 045), so a changed, removed or inserted entry breaks every fingerprint after
// it. One line: intact since when, or the first entry that is not. The full
// sentence -- including what the check cannot prove -- is in the tooltip.
export function RecordCheck() {
  const [check, setCheck] = useState(null);
  useEffect(() => {
    let live = true;
    api.get('/api/v1/history/verify')
      .then(r => { if (live) setCheck(r.data); })
      .catch(() => { if (live) setCheck({ failed: true }); });
    return () => { live = false; };
  }, []);

  if (!check) return <span style={{ fontSize: 11.5, color: MUTED }}>Checking the record…</span>;
  if (check.failed) return <span style={{ fontSize: 11.5, color: MUTED }}>The record could not be checked just now.</span>;
  const tip = [check.says, check.next].filter(Boolean).join('\n\n');
  return (
    <span title={tip} style={{ fontSize: 11.5, color: check.ok ? GREEN : RED, display: 'flex', alignItems: 'center', gap: 6, cursor: 'help' }}>
      <i className={`fa-solid ${check.ok ? 'fa-shield-halved' : 'fa-triangle-exclamation'}`} />
      {check.short}
    </span>
  );
}

// What the schedule has actually done.
//
// The board shows where work is now; this shows how it got there, and -- once
// the floor has finished enough jobs -- what the work really costs compared with
// what it was planned to.
export default function ScheduleHistoryModal({ onClose, onRestored }) {
  return (
    <div style={st.overlay} onClick={onClose}>
      <div style={st.card} onClick={e => e.stopPropagation()}>
        <div style={st.head}>
          <div style={st.title}>
            <i className="fa-solid fa-clock-rotate-left" style={{ color: TEAL }} />
            Schedule history
          </div>
          <button aria-label="Close" style={{ background: 'none', border: 'none', color: MUTED, fontSize: 16, cursor: 'pointer' }} onClick={onClose}>✕</button>
        </div>

        <div style={st.body}>
          {/* How fast parts really run against their plan lives in Insights now
              ("Cycle time vs plan", the director, 6 Oct 2026). */}
          <LogTab onRestored={onRestored} />
        </div>

        <div style={{ ...st.footer, justifyContent: 'space-between', alignItems: 'center' }}>
          <RecordCheck />
          <button style={st.btnGhost} onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

// ── what happened ───────────────────────────────────────────────────────────

const FILTERS = [
  ['', 'Everything'],
  ['completed,started', 'From the floor'],
  ['reflow,moved,resized,scheduled', 'Planning'],
  ['window,hold', 'Capacity'],
];

function LogTab({ onRestored }) {
  const [filter, setFilter]   = useState('');
  const [events, setEvents]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [busy, setBusy]       = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const { data } = await api.get('/api/v1/schedule-events', {
        params: { limit: 200, ...(filter ? { kind: filter } : {}) },
      });
      setEvents(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  async function restore(ev) {
    setBusy(ev.id); setError(null);
    try {
      await api.post(`/api/v1/reflow/undo/${ev.snapshot_id}`);
      onRestored && onRestored();
      await load();
    } catch (e) {
      setError(e.response?.data?.error || 'That change has already been put back.');
    } finally {
      setBusy(null);
    }
  }

  // Grouped by day, because "when" is how anyone looks for a change they
  // half-remember making.
  const days = useMemo(() => {
    const m = new Map();
    for (const e of events) {
      const k = dayLabel(e.at);
      if (!m.has(k)) m.set(k, []);
      m.get(k).push(e);
    }
    return [...m.entries()];
  }, [events]);

  return (
    <>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 16 }}>
        {FILTERS.map(([v, label]) => (
          <button key={v} onClick={() => setFilter(v)}
            style={{ padding: '5px 12px', fontSize: 12.5, borderRadius: 7, cursor: 'pointer',
                     background: filter === v ? `${TEAL}20` : 'transparent',
                     border: `1px solid ${filter === v ? TEAL : BORDER}`,
                     color: filter === v ? TEAL : DIM, fontWeight: filter === v ? 700 : 400 }}>
            {label}
          </button>
        ))}
      </div>

      {error && (
        <div style={{ marginBottom: 14, padding: '11px 13px', background: `${AMBER}12`,
                      border: `1px solid ${AMBER}44`, borderRadius: 9, fontSize: 13, color: DIM }}>
          {error}
        </div>
      )}

      {loading ? (
        <div style={{ color: MUTED, fontSize: 13 }}>Loading…</div>
      ) : events.length === 0 ? (
        <Empty
          title="Nothing recorded yet"
          body="The log starts from the moment it was switched on. Move a job, declare a shift window or finish a run on the floor and it will appear here." />
      ) : days.map(([day, list]) => (
        <div key={day} style={{ marginBottom: 20 }}>
          <div style={{ ...st.eyebrow, marginBottom: 9 }}>{day}</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {list.map(e => (
              <EventRow key={e.id} e={e} busy={busy === e.id} onRestore={() => restore(e)} />
            ))}
          </div>
        </div>
      ))}
    </>
  );
}

function EventRow({ e, busy, onRestore }) {
  const k = kindOf(e.kind);
  const drift = e.actual?.cycle_drift_pct;
  const machine = e.machines?.display_name || e.machines?.name;

  return (
    <div style={{ display: 'flex', gap: 11, padding: '10px 13px', background: SUNK,
                  border: `1px solid ${BORDER}`, borderRadius: 9, alignItems: 'flex-start' }}>
      <div style={{ width: 26, height: 26, borderRadius: 7, flexShrink: 0, marginTop: 1,
                    background: `${k.colour}1c`, border: `1px solid ${k.colour}3a`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <i className={`fa-solid ${k.icon}`} style={{ color: k.colour, fontSize: 11 }} />
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, color: TEXT, lineHeight: 1.5 }}>{e.summary}</div>
        <div style={{ fontSize: 11.5, color: MUTED, marginTop: 3, display: 'flex',
                      flexWrap: 'wrap', gap: '2px 10px' }}>
          <span style={{ fontVariantNumeric: 'tabular-nums' }}>{timeOf(e.at)}</span>
          {e.actor && <span>{e.actor}</span>}
          {e.source === 'floor' && <span style={{ color: GREEN }}>from the floor</span>}
          {machine && <span>{machine}</span>}
        </div>

        {/* A measured run is the point of the whole table, so it is shown rather
            than buried in the detail. */}
        {e.actual && (e.actual.cycle_s != null || e.actual.against_target_pct != null) && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 8px', marginTop: 7 }}>
            {e.planned?.cycle_s != null && e.actual.cycle_s != null && (
              <Chip label="cycle" value={`${e.actual.cycle_s}s vs ${e.planned.cycle_s}s`}
                    tone={drift == null ? MUTED : Math.abs(drift) < 5 ? MUTED : drift > 0 ? RED : GREEN} />
            )}
            {drift != null && Math.abs(drift) >= 5 && (
              <Chip label={drift > 0 ? 'slow by' : 'fast by'} value={`${Math.abs(drift)}%`}
                    tone={drift > 0 ? RED : GREEN} />
            )}
            {e.actual.against_target_pct != null && (
              <Chip label="of target" value={`${e.actual.against_target_pct}%`}
                    tone={e.actual.against_target_pct >= 99 ? GREEN : AMBER} />
            )}
          </div>
        )}
      </div>

      {e.snapshot_id && (
        <button onClick={onRestore} disabled={busy}
          style={{ ...st.btnGhost, padding: '5px 11px', fontSize: 11.5, flexShrink: 0,
                   color: AMBER, borderColor: `${AMBER}55`, opacity: busy ? 0.5 : 1 }}>
          {busy ? '…' : 'Restore'}
        </button>
      )}
    </div>
  );
}

function Chip({ label, value, tone }) {
  return (
    <span style={{ fontSize: 11.5, padding: '2px 8px', borderRadius: 5,
                   background: `${tone}14`, border: `1px solid ${tone}40`, whiteSpace: 'nowrap' }}>
      <span style={{ color: MUTED }}>{label} </span>
      <span style={{ color: tone, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{value}</span>
    </span>
  );
}

function Empty({ title, body }) {
  return (
    <div style={{ padding: '26px 20px', background: SUNK, border: `1px dashed ${BORDER}`,
                  borderRadius: 11, textAlign: 'center' }}>
      <div style={{ fontSize: 14, fontWeight: 700, color: DIM }}>{title}</div>
      <div style={{ fontSize: 13, color: MUTED, marginTop: 6, lineHeight: 1.6, maxWidth: 420,
                    marginLeft: 'auto', marginRight: 'auto' }}>{body}</div>
    </div>
  );
}
